import { type ActionFunctionArgs, json, redirect, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import {
  Form,
  Link,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import { ClockIcon, HistoryIcon, IndianRupeeIcon, PercentIcon} from "lucide-react";
import {  useMemo, useState } from "react";
import { ToWords } from 'to-words'
import { ErrorListCalculator } from "#app/components/forms";
import { Accordion, AccordionContent, AccordionItem, BadgeAccordionTrigger } from "#app/components/ui/accordion";
import { HoverBorderGradient } from "#app/components/ui/animatation/hover-border-gradient";
import { Icon } from "#app/components/ui/icon";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "#app/components/ui/table.tsx";
import { getUserId } from "#app/utils/auth.server";
import {type CompoundingTypeArgs, calculateInterest } from "#app/utils/calculators"
import { logger } from '#app/utils/logger'
import { getFdTitleFromName, getSocialMetas } from "#app/utils/seo";
import { Badge } from "#components/ui/badge"
import { Button } from "#components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "#components/ui/card.tsx";
import { Input } from "#components/ui/input.tsx";
import { Label } from "#components/ui/label.tsx";
import { RadioGroup, RadioGroupItem } from "#components/ui/radio-group.tsx"
import { Switch } from "#components/ui/switch"
import { FaqBadges } from "../_marketing+";

const IMAGE_BASE_URL = 'https://bhihtsmosmghzjnmhufc.supabase.co/storage/v1/object/public/superfd-images/bank-logos'

const defaultAmount = 100000
const defaultSuryodayAmount = 100000
const MIN_AMOUNT = 1000
const MAX_AMOUNT = 3_00_00_000

const toWords = new ToWords({
  localeCode: 'en-IN',
  converterOptions: {
    currency: true,
    ignoreDecimal: true,
    ignoreZeroCurrency: true,
    doNotAddOnly: false,
    currencyOptions: {
      // can be used to override defaults for the selected locale
      name: 'Rupee',
      plural: 'Rupees',
      symbol: '₹',
      fractionalUnit: {
        name: 'Paisa',
        plural: 'Paise',
        symbol: '',
      },
    },
  },
})

export const meta: MetaFunction<typeof loader> = ({ data, params}) => {
  const bankName = params.bankPage ?? ''
  const fdFirstName = bankName.split('-')[0]
  const {title, description,url, keywords, image } = getFdTitleFromName(fdFirstName, '')

  return [
    { viewport: 'width=device-width,initial-scale=1,viewport-fit=cover' },
		...getSocialMetas({
			keywords,
			url,
			image,
			title,
			description,
		}),
  ]
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const userId = await getUserId(request)

  // const userId = await requireUserId(request)
  const bankName = params.bankPage

  if(!bankName) return redirect('/fixed-deposit')

  logger.info({
		message: `/banks/${bankName},user/${userId}`,
		labels: { origin: 'user_page' },
	})

  return json({ bankData: BanksData[`${bankName.split('-')[0]}`], bankComparisonData: BanksComparisonData })

};

export async function action({ request, params }: ActionFunctionArgs) {

  return redirect('/')
}


export default function Index() {
  const location = useLocation()
  const pageData = useLoaderData<typeof loader>()
  const fdData = pageData.bankData
  var fdFirstName = fdData?.fdFirstName ?? ''

  const [amount, setAmount] = useState<string>(() => {
    return fdFirstName === 'Suryoday' ?   `${new Intl.NumberFormat('en-In').format(defaultSuryodayAmount)}` : `${new Intl.NumberFormat('en-In').format(defaultAmount)}`
  })
  const [amtError, setAmtError] = useState<string[]>(() => {

      if( MIN_AMOUNT && (amount === '' || (`${new Intl.NumberFormat('en-In').format(parseInt((amount).replaceAll(',','')))}` === 'NaN')) ){
        return [`Min. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_AMOUNT.toString()).replaceAll(',','')))}`]
      }else{

        if(MIN_AMOUNT && Number((amount).replaceAll(',','')) < MIN_AMOUNT){
          return [`Min. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_AMOUNT.toString()).replaceAll(',','')))}`]
        }else if(MAX_AMOUNT && Number((amount).replaceAll(',','')) > MAX_AMOUNT){
          return [`Max. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MAX_AMOUNT.toString()).replaceAll(',','')))}`]
        }else{
          return []
        }
      }
  })
  const [tenure, setTenure] = useState(() => {
    return fdData?.fds[0] ?? {
      id: 1,
      tenureYearFormat: '18M',
      generalInterest: 7.25,
      seniorInterest: 7.75,
      highlight: 'normal'
    }
  })
  const [isSeniorCitizen, setIsSeniorCitizen] = useState<boolean>(false)
  // const [isMinTenure, setIsMinTenure] = useState<boolean>(true)

  const calculatedAmounts = calculateInterest({amount: Number(amount.replaceAll(',','')), interest: isSeniorCitizen ? tenure?.seniorInterest : tenure?.generalInterest, tenure: tenure?.tenureYearFormat, compoundingType: fdData?.fdCompoundingType as CompoundingTypeArgs})

  const sortedBanks = useMemo(() => {
    return pageData.bankComparisonData.sort(
      (a, b) => 
        (b.interestRate + (isSeniorCitizen ? b.seniorCitizenIncrement : 0)) - (a.interestRate + (isSeniorCitizen ? a.seniorCitizenIncrement : 0)))
      .slice(0, 15);
  }, [isSeniorCitizen, pageData.bankComparisonData]);

  if(fdFirstName === ''){
    return (
    <div className="container flex flex-col items-center justify-center p-20 text-h2">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <h1>We can't find this page:</h1>
            <pre className="whitespace-pre-wrap break-all text-body-lg">
              {location.pathname}
            </pre>
          </div>
          <Link to="/" className="text-body-md underline">
            <Icon name="arrow-left">Back to home</Icon>
          </Link>
        </div>
        <div>
          <Button><Link to="/">Home</Link></Button>
        </div>
    </div>
    )
  }

  return (
    <div className="p-4 720p:py-2 md:container h-full">
    {/* // <PageBody>
    //   <PageContainer> */}
     
      <div className="py-4 720p:py-2 flex gap-4 flex-col md:flex-row pb-80"> { /* py-4 720p:py-2 flex gap-4 flex-col md:flex-row */} { /*relative py-6 lg:gap-10 lg:py-8 xl:grid xl:grid-cols-[1fr_400px] */}

        {/* basis-2/3 space-y-4 order-2 md:order-1 */} {/* mx-auto w-full min-w-0 max-w-3x */}
        <div className="basis-2/3 space-y-4 order-2 md:order-1">

        <div className="py-4 space-y-8">
          <div className="pb-6 flex gap-4 items-center self-center shadow-sm border-b">
            <img
                src={fdData.logo}
                alt={`${fdData.fdFirstName} logo`}
                className="h-12 w-12 720p:h-8 720p:w-8 object-cover"
            />
            <h2 className="text-3xl font-bold tracking-tight">{`${fdData.fdFirstName} FD Interest Rates`}</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">

            {
              fdData.fdTopFeatures.map((feature,i) => (
                <div key={`feature-${feature.value}`} className="flex items-start space-x-4">
                  <div className="flex justify-center items-center h-full">
                    <div className="">
                      {
                        FdFeatureIcons[i]['icon']
                      }
                    </div>
                   
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{feature.value}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{feature.title}</p>
                  </div>
                </div>
              ))
            }
        
        </div>
        </div>

        <Card className="p-2">
        <CardHeader className="px-2 md:p-6 pb-4 md:pb-2">
          
          <CardTitle>
            {`${fdData.fdFirstName} FD Rates`}
          </CardTitle>
          <div className="pt-4 flex flex-row items-center justify-end">
          {/* <div>
            <div
              className="group inline-flex items-center gap-2"
              data-state={isMinTenure ? "min" : "max"}
            >
              <span
                id="switch-min-tenure-label"
                className="flex-1 cursor-pointer text-right text-sm font-medium group-data-[state=max]:text-muted-foreground/70"
                onClick={() => setIsMinTenure(true)}
              >
                Min
              </span>
              <Switch
                id="switch-09"
                checked={!isMinTenure}
                onCheckedChange={() => setIsMinTenure(current => !current)}
                aria-labelledby="switch-min-tenure-label switch-max-tenure-label"
              />
              <span
                id="switch-max-tenure-label"
                className="flex-1 cursor-pointer text-left text-sm font-medium group-data-[state=min]:text-muted-foreground/70"
                onClick={() => setIsMinTenure(false)}
              >
                Max
              </span>
            </div>
          </div> */}

          
          <div className="flex justify-between align-center">
            <div className="flex items-center space-x-2">
              <Switch id="senior-citizen" name="isSenior" checked={isSeniorCitizen} onCheckedChange={() => setIsSeniorCitizen(current => !current)} />
              <Label htmlFor="senior-citizen">Senior Citizen</Label>
            </div>
          </div>

          </div>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          <Table>
            <TableHeader>
              <TableRow>
              <TableHead className="">{`Tenure`}</TableHead>
                <TableHead>{`${isSeniorCitizen ? 'Senior' : 'General'} Interest`}</TableHead>
                {/* <TableHead className="">{`${isMinTenure ? 'Maturity Amount (Min)' : 'Maturity Amount (Max)'}`}</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {
                fdData.interestRates.map(interest => (
                  <TableRow key={interest.tenure}>
                    <TableCell className="font-medium">{interest.tenure}</TableCell>
                    <TableCell className={`${interest.highlight === 'Max Return' ? 'font-medium text-gain' : 'font-medium' }`}>{`${isSeniorCitizen ? interest.seniorInterest : interest.generalInterest}%`}</TableCell>
                    {/* <TableCell className={`${interest.highlight === 'Max Return' ? 'font-medium text-gain' : 'font-medium' }`}>&#8377; {`${new Intl.NumberFormat('en-In').format(calculateInterest({amount: Number(amount.replaceAll(',','')), interest: isSeniorCitizen ? interest.seniorInterest : interest.generalInterest, tenure: isMinTenure ? interest.minTenure : interest.maxTenure, compoundingType: fdData.fdCompoundingType as CompoundingTypeArgs}).mAmt)}`}</TableCell> */}
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        
        </CardContent>
        </Card>

        <Card className="p-2">
        <CardHeader className="px-2 md:p-6">
          <CardTitle>
            {`${fdData.fdFirstName} FD Features`}
          </CardTitle>
        </CardHeader>
        <CardContent className=" px-2 md:px-6 space-y-2">

        {
          fdData.features.map(feature => (
            <div key={feature} className="flex space-x-2">
            <div className="">
              <Icon name="check-circled" className="h-6 w-6 text-gain" /> 
            </div>
            <div>
              {feature}
            </div>
          </div>
          ))

        }

        </CardContent>
        </Card>

        <Card className="p-2">
        <CardHeader className="px-2 md:p-6">
          <CardTitle>
            {`Unity vs ${fdData.fdFirstName} Comparison`}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="">Features</TableHead>
                <TableHead>Unity Bank</TableHead>
                <TableHead>{`${fdData.fdFirstName} Bank`}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
            
              <TableRow>
                <TableCell className="font-medium">Interest Rate</TableCell>
                <TableCell className="font-medium text-gain">9.1% p.a.</TableCell>
                <TableCell className="font-medium">{`${fdData.highestInterest}% p.a.`}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">Minimum Deposit</TableCell>
                <TableCell className="font-medium">&#8377; {`${new Intl.NumberFormat('en-In').format(1000)}`}</TableCell>
                <TableCell className="font-medium">&#8377; {`${new Intl.NumberFormat('en-In').format(fdData.minAmount)}`}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">Instant Booking</TableCell>
                <TableCell className="font-medium">
                  <Icon name="check-circled" className="h-6 w-6 text-gain" /> 
                </TableCell>
                <TableCell className="font-medium">
                  <Icon name="cross-circled" className="h-6 w-6 text-red-500" />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">Book FD Without Bank Account</TableCell>
                <TableCell className="font-medium">
                  <Icon name="check-circled" className="h-6 w-6 text-gain" /> 
                </TableCell>
                <TableCell className="font-medium">
                  <Icon name="cross-circled" className="h-6 w-6 text-red-500" />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="font-medium">RBI Insurance</TableCell>
                <TableCell className="font-medium">Insured upto &#8377;5L</TableCell>
                <TableCell className="font-medium">Insured upto &#8377;5L</TableCell>
              </TableRow>
                
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <div>
                    <Link to={'#heroSection'} className="data-te-smooth-scroll-init animate-slidein [--slidein-delay:300ms] opacity-0 no-underline group cursor-pointer relative shadow-2xl shadow-zinc-900 rounded-full p-px text-xs font-semibold leading-6 text-white inline-block">
                    <Button
                    className="animate-shimmer text-[#0f172a] bg-[linear-gradient(110deg,#F8FAFC,45%,#22c55e,55%,#F8FAFC)] bg-[length:200%_100%] px-6 font-medium transition-colors"
                    >Book Now</Button>
                    </Link>
                  </div>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        
        </CardContent>
        </Card>

        <Card className="p-2">
        <CardHeader className="px-2 md:p-6 pb-4 md:pb-2">
          <CardTitle>
              Top 15 Banks by Interest Rate
          </CardTitle>
          
          <div className="pt-4 flex flex-row items-center justify-end">
            <div className="flex justify-between align-center">
              <div className="flex items-center space-x-2">
                <Switch id="senior-citizen" name="isSenior" checked={isSeniorCitizen} onCheckedChange={() => setIsSeniorCitizen(current => !current)} />
                <Label htmlFor="senior-citizen">Senior Citizen</Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="">Bank</TableHead>
                <TableHead>{`${isSeniorCitizen ? 'Senior' : 'General'} Interest`}</TableHead>
                <TableHead className="">Tenure</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {
                sortedBanks.map(bank => (
                  <TableRow key={bank.name}>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <img
                          src={bank.logo}
                          alt={bank.name}
                          className="h-6 w-6 mr-2 object-cover"
                        />
                        <span>{bank.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className='font-medium'>{`${isSeniorCitizen ? (bank.interestRate + bank.seniorCitizenIncrement) : bank.interestRate}%`}</TableCell>
                    <TableCell className="font-medium">{bank.tenure}</TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        
        </CardContent>
        </Card>

        <div className="lg:hidden w-full max-w-md mx-auto py-4">
          {/* <h2 className="text-lg font-medium mb-4 px-4">FD Calculators</h2> */}
          <div className="border rounded-lg divide-y">
                <Link
                key={'fd-calculator'}
                to={`/calculator`}
                className="block px-4 py-3 text-primary font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {`FD Calculator`}
              </Link>
            {calculators.map((calculator) => (
              <Link
                key={calculator.name}
                to={calculator.href}
                className="block px-4 py-3 hover:bg-accent hover:text-accent-foreground transition-colors"
                reloadDocument
              >
                {calculator.name}
              </Link>
            ))}
          </div>
        </div>

        <Card className="p-2">
        <CardHeader className="px-2 md:p-6">
          <CardTitle>
              FAQs
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
        <div className="flex flex-col justify-center space-y-4">
          {/* <Accordion className="w-full" collapsible type="single">
            {
              faqData['Shriram'].map(faq => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: `${faq.answer}`}}/>
                  </AccordionContent>
                </AccordionItem>
              ))
            }
          </Accordion> */}
          <Accordion className="w-full" collapsible type="single">
            {
              faqData[fdData.fdFirstName].map(faq => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                  <BadgeAccordionTrigger className="text-left text-sm font-medium flex items-center">
                  
                  <div className="min-w-[70px] md:min-w-[80px] mr-2">
                    {
                      FaqBadges(faq.type)
                    }
                  </div>
                  
                  <div>
                  {faq.question}
                  </div>

                  </BadgeAccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: `${faq.answer}`}}/>
                  </AccordionContent>
                </AccordionItem>
              ))
            }
          </Accordion>
        </div>
        </CardContent>
        </Card>

        </div>
        {/*  basis-1/3 md:order-2 */} {/* hidden xl:block */}
        <div className="basis-1/3 md:order-2">
          {/* <div className="relative rounded-xl">

           <BorderBeam className="" size={250} duration={12} colorFrom="#31bdc6" colorTo="#3178c6" borderWidth={2} /> */}

          <div className="sticky top-20 -mt-4 md:h-[calc(100vh-3.5rem)] pt-4">
          <div className="no-scrollbar h-[560px] lg:h-[700px] overflow-auto">

          <HoverBorderGradient
           containerClassName="rounded-xl w-full"
           duration={1.5}
           as="div"
           className="w-full">
          <Form method="POST" >
            <Card className="p-2 max-w-md">
                <CardHeader className="720p:hidden">
                  <CardTitle>Calculate Returns</CardTitle>
                </CardHeader>
                <CardContent className="px-2 md:p-6">
                    <div>
                        <Label htmlFor="amount">Investment Amount</Label>
                        <Input 
                          type="text" 
                          id="amount" 
                          placeholder="Enter the amount"
                          name="amount" 
                          defaultValue={defaultAmount}
                          value={amount}
                          min={MIN_AMOUNT}
                          maxLength={15}
                          onChange={e => {

                            if( MIN_AMOUNT && (e.target.value === '' || (`${new Intl.NumberFormat('en-In').format(parseInt((e.target.value).replaceAll(',','')))}` === 'NaN')) ){
                              setAmount('')
                              setAmtError([`Min. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_AMOUNT.toString()).replaceAll(',','')))}`])
                            }else{
                              //console.log('value, amount : ', e.target.value, amount)
                              if(MIN_AMOUNT && Number((e.target.value).replaceAll(',','')) < MIN_AMOUNT){
                                setAmtError([`Min. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MIN_AMOUNT.toString()).replaceAll(',','')))}`])
                              }else if(MAX_AMOUNT && Number((e.target.value).replaceAll(',','')) > MAX_AMOUNT){
                                setAmtError([`Max. Amount is Rs. ${new Intl.NumberFormat('en-In').format(parseInt((MAX_AMOUNT.toString()).replaceAll(',','')))}`])
                              }else{
                                setAmtError([])
                              }
                              setAmount(`${new Intl.NumberFormat('en-In').format(parseInt((e.target.value).replaceAll(',','')))}`)
                            }

                          }}
                        />
                        <div className="min-h-[16px] px-4 pb-1 pt-1">
                          {
                            amtError.length > 0 
                              ? <ErrorListCalculator errors={amtError} /> 
                              : (
                                <p className="text-xs text-muted-foreground">
                                  {toWords.convert(parseInt(amount.replaceAll(',','')))}
                                </p>
                              )
                          }
                        </div>
                    </div>

                    <div className="py-2 max-w-md">
                    <Label htmlFor="tenure">Tenure</Label>
                    <RadioGroup 
                      id="tenure"
                      name="tenure"
                      defaultValue={`${tenure.id}`}
                      className="grid grid-cols-2 gap-4 mt-2" 
                      onValueChange={(e: string) => {
                         let changedTenure = fdData.fds.find(current => `${current.id}` === e)
                         if(changedTenure){
                          setTenure(changedTenure)
                         }
                         
                      }}>
                      {
                        fdData.fds.map((value,i) => (
                        <div key={value.id} className="">
                          <RadioGroupItem value={`${value.id}`} id={`${value.id}`} className="peer sr-only" />
                          <Label htmlFor={`${value.id}`}
                          className="flex relative flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"           
                          > 
                            { value.highlight !== 'normal' 
                                 ? (<div className="self-end absolute right-0 top-0.5 rounded-none">
                                      {
                                        value.highlight === 'Most Popular'
                                          ? <Badge variant={"most_popular"}>{value.highlight}</Badge>
                                          : <Badge variant={"max_return"}>{value.highlight}</Badge>
                                      }
                                    </div>
                                  )
                                 : (null)
                            }
                            <span className="font-semibold py-2">
                              {isSeniorCitizen 
                                ? <span>{`${value.seniorInterest}%`}<span className="text-xs font-light text-muted-foreground"> p.a.</span></span>
                                : <span>{`${value.generalInterest}%`}<span className="text-xs font-light text-muted-foreground"> p.a.</span></span>

                              }
                            </span>
                            <span className="font-light">{fdFirstName === 'Suryoday' ? value.tenureYearFormat === '18M' ? '1Y 6M' : value.tenureYearFormat  : value.tenureYearFormat}</span>
                          </Label>
                        </div>))
                      }
                  </RadioGroup>
                  </div>

                    <div className="py-4 flex justify-between align-center">
                      <div className="flex items-center space-x-2">
                        <Switch id="senior-citizen" name="isSenior" checked={isSeniorCitizen} onCheckedChange={() => setIsSeniorCitizen(current => !current)} />
                        <Label htmlFor="senior-citizen">Senior Citizen</Label>
                      </div>


                    </div>

                    <div className="py-2 flex justify-between">
                      <div>
                        <div className="">Rs <span className="text-lg text-green-500 font-semibold">{new Intl.NumberFormat('en-In').format(calculatedAmounts.mAmt)}</span></div>
                        <div className="text-xs font-light">Maturity Amount</div>
                      </div>

                      <div>
                        <div className="">Rs <span className="text-lg text-green-500 font-semibold">{new Intl.NumberFormat('en-In').format(calculatedAmounts.fdGains)}</span></div>
                        <div className="text-xs font-light">Total Gains</div>
                      </div>
                    </div>

                </CardContent>
                <CardFooter className="px-2 md:p-6">
                  { 
                    
                    <Button 
                      disabled={amtError.length > 0} 
                      className="w-full text-slate-900 animate-shimmer bg-[linear-gradient(110deg,#F8FAFC,45%,#22c55e,55%,#F8FAFC)] bg-[length:200%_100%] px-6 font-medium transition-colors" 
                      type="submit"
                    >
                      Explore Higher Return FDs
                    </Button>
               
                  }
                </CardFooter>
            </Card>
            </Form>
          </HoverBorderGradient>

          </div>

          <div className="hidden lg:block w-full max-w-md mx-auto py-4">
            {/* <h2 className="text-lg font-medium mb-4 px-4">FD Calculators</h2> */}
            <div className="border rounded-lg divide-y">
                 <Link
                  key={'fd-calculator'}
                  to={`/calculator`}
                  className="block px-4 py-3 text-primary font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {`FD Calculator`}
                </Link>
              {calculators.map((calculator) => (
                <Link
                  key={calculator.name}
                  to={calculator.href}
                  className="block px-4 py-3 hover:bg-accent hover:text-accent-foreground transition-colors"
                  reloadDocument
                >
                  {calculator.name}
                </Link>
              ))}
            </div>
          </div>

          
          </div>


          {/* </div> */}
        </div>
      </div>

      
      {/* </PageContainer>
    </PageBody> */}
    </div>
  )
}

export function ErrorBoundary() {
  const error = useRouteError();

  // when true, this is what used to go to `CatchBoundary`
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>Oops</h1>
        <p>Status: {error.status}</p>
        <p>{error.data.message}</p>
      </div>
    );
  }

  // Don't forget to typecheck with your own logic.
  // Any value can be thrown, not just errors!
  let errorMessage = "Unknown error";
  if (error instanceof Error) {
    errorMessage = error.message;
  }

  return (
    <div className="relative min-h-screen bg-white sm:flex sm:items-center sm:justify-center">
      <div className="mx-auto flex max-w-2xl flex-col gap-4 rounded-md bg-slate-50 p-12">
        <h1 className="text-3xl font-bold text-slate-900">Uh oh ...</h1>
        <p className="text-slate-700">Something went wrong.</p>
        <pre className="overflow-scroll rounded-md border border-slate-300 bg-white p-4 text-red-500">
          {errorMessage}
        </pre>
      </div>
    </div>
  );
}

const FdFeatureIcons: Record<string,any>[] = [
  {
    name: 'Percent Icon',
    icon: <PercentIcon className="w-8 h-8 self-center" />,
  },
  {
    name: 'Clock Icon',
    icon: <ClockIcon className="w-8 h-8 self-center" />,
  },
  {
    name: 'Indian Rupee Icon',
    icon: <IndianRupeeIcon className="w-8 h-8 self-center" />,
  },
  {
    name: 'history Icon',
    icon: <HistoryIcon className="w-8 h-8 self-center" />,
  },
  

]

type BankComparisonType = {
  name: string, logo: string, seniorCitizenIncrement: number, interestRate: number, tenure: string
}

const BanksComparisonData: Array<BankComparisonType> = [
  { "name": "Unity", "logo": `${IMAGE_BASE_URL}/unity.png`, "seniorCitizenIncrement": 0.5, interestRate: 9.0, tenure: '1001 Days' },
  { "name": "Suryoday", "logo": `${IMAGE_BASE_URL}/Suryoday.webp`, "seniorCitizenIncrement": 0.45, interestRate: 8.65, tenure: '2 Years 2 Days' },
  { "name": "Bandhan", "logo": `${IMAGE_BASE_URL}/bandhan.webp`, "seniorCitizenIncrement": 0.5, interestRate: 8.0, tenure: '1 Year 9 Months' },
  { "name": "IndusInd", "logo": `${IMAGE_BASE_URL}/indusind.webp`, "seniorCitizenIncrement": 0.5, interestRate: 7.99, tenure: '1 Year 5 Months' },
  { "name": "Utkarsh", "logo": `${IMAGE_BASE_URL}/utkarsh.webp`, "seniorCitizenIncrement": 0.60, interestRate: 8.50, tenure: '2 Years' },
  { "name": "AXIS", "logo": `${IMAGE_BASE_URL}/axis.webp`, "seniorCitizenIncrement": 0.50, interestRate: 7.25, tenure: '15 Months' },
  { "name": "Northeast", "logo": `${IMAGE_BASE_URL}/northeast.webp`, "seniorCitizenIncrement": 0.5, interestRate: 9.0, tenure: '555 Days' },
  { "name": "Shivalik", "logo": `${IMAGE_BASE_URL}/shivalik.webp`, "seniorCitizenIncrement": 0.5, interestRate: 8.55, tenure: '18 Months' },
  { "name": "SBI", "logo": `${IMAGE_BASE_URL}/sbi.png`, "seniorCitizenIncrement": 0.5, interestRate: 7.25, tenure: '444 Days' },
  { "name": "HDFC", "logo": `${IMAGE_BASE_URL}/hdfc.png`, "seniorCitizenIncrement": 0.5, interestRate: 7.40, tenure: '4 Years 7 Months' },
  { "name": "ICICI", "logo": `${IMAGE_BASE_URL}/icici.png`, "seniorCitizenIncrement": 0.5, interestRate: 7.25, tenure: '15 Months' },
  { "name": "KOTAK", "logo": `${IMAGE_BASE_URL}/kotak.webp`, "seniorCitizenIncrement": 0.5, interestRate: 7.40, tenure: '390 Days' },
  { "name": "DCB", "logo": `${IMAGE_BASE_URL}/dcb.webp`, "seniorCitizenIncrement": 0.5, interestRate: 8.05, tenure: '19 Months'},
  { "name": "RBL", "logo": `${IMAGE_BASE_URL}/rbl.webp`, "seniorCitizenIncrement": 0.5, interestRate: 8.10, tenure: '500 Days' },
  { "name": "AU", "logo": `${IMAGE_BASE_URL}/au.webp`, "seniorCitizenIncrement": 0.5, interestRate: 8.0, tenure: '18 Months' }
]

type FAQTagType = 'safety' | 'booking' | 'reward' | 'support' | 'vkyc'

const faqData: Record<string, Array<{id: number, question: string, answer: string, type: FAQTagType}>> = {
  "SBI": [
    {
      id: 1,
      type: 'booking',
      question: "What is the highest interest rate SBI offers on fixed deposits?",
      answer: `
      <div>
      <div>Currently, SBI offers a maximum interest rate of 7.25% p.a. for general citizens on fixed deposits, through the Amrit Vrishti scheme, a 444-day FD.</div>
      <div style="padding-top: 5px">For senior citizens, the same scheme offers interest at 7.75% p.a. </div>
      </div>
      `
    },
    {
      id: 2,
      type: 'booking',
      question: "Can I open an SBI Fixed Deposit Account online?",
      answer: `
      <div>
      <div>Yes, SBI fixed deposits can be opened online through SBI YONO mobile app and SBI internet banking.</div>
      <div style="padding-top: 5px">Note that you need to have an active savings, current or an OD account with SBI before you can book an FD with them.</div>
      </div>
      `
    },
    {
      id: 3,
      type: 'booking',
      question: "Who is eligible to invest in SBI Fixed Deposits?",
      answer: `
      <div>
      <div style="padding-top: 5px;font-weight: bold">Individual Investors:</div>
      <div style="padding-top: 5px">Resident Indian citizens, Non-Resident Indians (NRIs) with NRE or NRO accounts, Overseas Citizens of India (OCI), Persons of Indian Origin (PIO), Minors (through guardians)</div>

      <div style="padding-top: 5px;font-weight: bold">Non-Individual Investors:</div>
      <div style="padding-top: 5px">Sole proprietorships, Hindu Undivided Families (HUFs), Partnerships, Companies (public and private limited), Trusts, Societies, Clubs, Associations</div>

      <div style="padding-top: 5px;font-weight: bold">Additional Eligibility Criteria</div>
      <div style="padding-top: 5px">
       <ol style="padding-left: 5px">
        <li>Individuals must be at least 18 years old to open an FD independently.</li>
        <li>For tax-saving FD schemes, only resident individuals and HUFs are eligible.</li>
       </ol>
      </div>
      </div>
      `
    },
    {
      id: 4,
      type: 'booking',
      question: "Does SBI have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
      answer: `
      <div>
      <div>The minimum deposit amount for SBI's tax-saving fixed deposit scheme is ₹1,000 and investments up to ₹1.5 lakh per financial year are eligible for tax deduction under Section 80C of the Income Tax Act. These deposits have a lock-in period of 5 years.</div>
      </div>
      `
    },
    {
      id: 5,
      type: 'booking',
      question: "Can I prematurely withdraw my SBI FD? If yes, are there any charges applicable?",
      answer: `
      <div>
      <div>Yes, a premature withdrawal facility is available for SBI FDs.</div>
      <div>
      Premature withdrawal incurs an interest rate penalty of 0.50% on deposits up to ₹5L and 1% on deposits above ₹5L.
      </div>
      </div>
      `
    }
],
"HDFC": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate HDFC Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, HDFC Bank offers a maximum interest rate of 7.4% p.a. for general citizens on fixed deposits, for a tenure of 4 Years 7 months 1 Day to less than 5 Years.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.9% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an HDFC Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, HDFC Bank fixed deposits can be opened online through NetBanking. You need to log into your NetBanking account, go to the TRANSACT section, choose Open Fixed Deposits, and follow the steps to create your FD.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in HDFC Bank Fixed Deposits?",
    answer: `
    <div>
    <div>HDFC Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does HDFC Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, HDFC Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹1000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my HDFC Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for HDFC Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"ICICI": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate ICICI Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, ICICI Bank offers a maximum interest rate of 7.25% p.a. for general citizens on fixed deposits, for tenures of 15 months to less than 18 months.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.75% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an ICICI Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, ICICI Bank fixed deposits can be opened online through internet banking or the ICICI Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in ICICI Bank Fixed Deposits?",
    answer: `
    <div>
    <div>ICICI Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does ICICI Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, ICICI Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹10,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my ICICI Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for ICICI Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"AXIS": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate Axis Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, Axis Bank offers a maximum interest rate of 7.25% p.a. for general citizens on fixed deposits, for tenures of 15 months to less than 18 months.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.75% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an Axis Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, Axis Bank fixed deposits can be opened online through internet banking or the Axis Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in Axis Bank Fixed Deposits?",
    answer: `
    <div>
    <div>Axis Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does Axis Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, Axis Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my Axis Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for Axis Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"KOTAK": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate Kotak Mahindra Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, Axis Bank offers a maximum interest rate of 7.40% p.a. for general citizens on fixed deposits, for tenures of 390 days to less than 23 months.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.9% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an Kotak Mahindra Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, Kotak Mahindra Bank fixed deposits can be opened online through internet banking or the Kotak Mahindra Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in Kotak Mahindra Bank Fixed Deposits?",
    answer: `
    <div>
    <div>Kotak Mahindra Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does Kotak Mahindra Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, Kotak Mahindra Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my Kotak Mahindra Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for Kotak Mahindra Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 0.50% to 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"IndusInd": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate IndusInd Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, IndusInd Bank offers a maximum interest rate of 7.99% p.a. for general citizens on fixed deposits, for tenures of 1 Year 5 Months to less  1 Year 5 Months.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 8.49% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an IndusInd Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, IndusInd Bank fixed deposits can be opened online through internet banking or the IndusInd Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in IndusInd Bank Fixed Deposits?",
    answer: `
    <div>
    <div>IndusInd Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does IndusInd Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, IndusInd Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹10,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my IndusInd Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for IndusInd Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"Bank of Baroda": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate Bank of Baroda offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, Bank of Baroda offers a maximum interest rate of 7.15% p.a. for general citizens on fixed deposits, for tenures of 2 years to less than 3 years.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.65% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an Bank of Baroda Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, Bank of Baroda fixed deposits can be opened online through internet banking or the Bank of Baroda mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in Bank of Baroda Fixed Deposits?",
    answer: `
    <div>
    <div>Bank of Baroda FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does Bank of Baroda have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, Bank of Baroda offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my Bank of Baroda FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for Bank of Baroda FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"Canara": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate Canara Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, Canara Bank offers a maximum interest rate of 7.4% p.a. for general citizens on fixed deposits, for tenures of 3 Years to less thatn 5 Years.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.9% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an Canara Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, Canara Bank fixed deposits can be opened online through internet banking or the Canara Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in Canara Bank Fixed Deposits?",
    answer: `
    <div>
    <div>Canara Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does Canara Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, Canara Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my Canara Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for Canara Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"PNB": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate PNB offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, PNB offers a maximum interest rate of 7.25% p.a. for general citizens on fixed deposits, for a tenure of 400 days.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.75% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an PNB Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, PNB fixed deposits can be opened online through internet banking or the PNB mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in PNB Fixed Deposits?",
    answer: `
    <div>
    <div>PNB FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does PNB have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, PNB offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my PNB FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for PNB FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
"IDBI": [
  {
    id: 1,
    type: 'booking',
    question: "What is the highest interest rate IDBI Bank offers on fixed deposits?",
    answer: `
    <div>
    <div>Currently, IDBI Bank offers a maximum interest rate of 7.40% p.a. for general citizens on fixed deposits, for a tenure of 555 days.</div>
    <div style="padding-top: 5px">For senior citizens, the highest rate is 7.90% p.a. for the same tenure.</div>
    </div>
    `
  },
  {
    id: 2,
    type: 'booking',
    question: "Can I open an IDBI Bank Fixed Deposit Account online?",
    answer: `
    <div>
    <div>Yes, IDBI Bank fixed deposits can be opened online through internet banking or the IDBI Bank mobile app.</div>
    </div>
    `
  },
  {
    id: 3,
    type: 'booking',
    question: "Who is eligible to invest in IDBI Bank Fixed Deposits?",
    answer: `
    <div>
    <div>IDBI Bank FDs are available to resident individuals, NRIs, and entities like HUFs, sole proprietorships, partnership firms, companies, trusts, clubs, and associations.</div>
    </div>
    `
  },
  {
    id: 4,
    type: 'booking',
    question: "Does IDBI Bank have a tax-saving FD scheme? If so, what is the minimum deposit amount?",
    answer: `
    <div>
    <div>Yes, IDBI Bank offers a tax-saving FD scheme. The minimum deposit amount is ₹1,000 and the maximum amount eligible for tax deduction is ₹1.5 lakh per financial year.</div>
    </div>
    `
  },
  {
    id: 5,
    type: 'booking',
    question: "Can I prematurely withdraw my IDBI Bank FD? If yes, are there any charges applicable?",
    answer: `
    <div>
    <div>Yes, premature withdrawal is allowed for IDBI Bank FDs, except for tax-saving FDs. The bank typically charges a penalty of 1% on the applicable interest rate for premature withdrawals.</div>
    </div>
    `
  }
],
}

export const calculators = [
  { name: "SBI FD Calculator", href: "/fixed-deposit/sbi-bank-fd-rates" },
  { name: "HDFC FD Calculator", href: "/fixed-deposit/hdfc-bank-fd-rates" },
  { name: "ICICI FD Calculator", href: "/fixed-deposit/icici-bank-fd-rates" },
  { name: "Axis Bank FD Calculator", href: "/fixed-deposit/axis-bank-fd-rates" },
  { name: "Kotak Mahindra Bank FD Calculator", href: "/fixed-deposit/kotak-bank-fd-rates" },
  { name: "IndusInd Bank FD Calculator", href: "/fixed-deposit/indusind-bank-fd-rates" },
  { name: "Bank of Baroda FD Calculator", href: "/fixed-deposit/bank_of_baroda-bank-fd-rates" },
  { name: "Canara Bank FD Calculator", href: "/fixed-deposit/canara-bank-fd-rates" },
  { name: "PNB FD Calculator", href: "/fixed-deposit/pnb-bank-fd-rates" },
  { name: "IDBI Bank FD Calculator", href: "/fixed-deposit/idbi-bank-fd-rates" },
]

type FdType = {
  id: number,
  tenureYearFormat: string,
  generalInterest: number,
  seniorInterest: number,
  highlight: string
}

type InterestType = {
  tenure: string, 
  generalInterest: number, 
  seniorInterest: number, 
  minTenure: string, 
  maxTenure: string, 
  highlight: string, 
}

type BankDataType = {
  fdFirstName: string,
  key: string,
  logo: string,
  fdCompoundingType: CompoundingTypeArgs,
  minAmount: number,
  highestInterest: number,
  fdTopFeatures: Record<string, string>[],
  fds: Array<FdType>,
  interestRates: Array<InterestType>,
  features: Array<string>
}

const BanksData: Record<string, BankDataType> = {
  "sbi": {
    fdFirstName: 'SBI',
    key: 'sbi',
    logo: `${IMAGE_BASE_URL}/sbi.png`,
    fdCompoundingType: 'quarterly',
    minAmount: 1000,
    highestInterest: 7.75,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.50% - 7.75%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "1,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '1Y',
        generalInterest: 6.8,
        seniorInterest: 7.3,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '400D',
        generalInterest: 7.1,
        seniorInterest: 7.6,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '444D',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'Max Return'
      },
      {
        id: 4,
        tenureYearFormat: '2Y',
        generalInterest: 7,
        seniorInterest: 7.5,
        highlight: 'normal'
      }
    ],
    interestRates : [
      {tenure: "7 - 45 Days", generalInterest: 3.50, seniorInterest: 4.0, minTenure: '7D', maxTenure: '15D', highlight: 'normal' },
      {tenure: "46 - 179 Days", generalInterest: 5.50, seniorInterest: 6.0, minTenure: '46D', maxTenure: '179D', highlight: 'normal' },
      {tenure: "180 - 210 Days", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '180D', maxTenure: '210D', highlight: 'normal' },
      {tenure: "211 Days - 1 Year", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '211D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 Year - 2 Years", generalInterest: 6.80, seniorInterest: 7.3, minTenure: '1Y', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "400 Days (Amrit Kalash scheme)", generalInterest: 7.10, seniorInterest: 7.6, minTenure: '400D', maxTenure: '400D', highlight: 'normal' },
      {tenure: "444 Days (Amrit Vrishti scheme)", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '444D', maxTenure: '444D', highlight: 'Max Return' },
      {tenure: "2 Years - 3 Years", generalInterest: 7.00, seniorInterest: 7.5, minTenure: '2Y', maxTenure: '3Y', highlight: 'normal' },
      {tenure: "3 Years - 5 Years", generalInterest: 6.75, seniorInterest: 7.25, minTenure: '3Y', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 Years - 10 Years", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "The SBI FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in SBI is Rs 1000 and there is no limit on the maximum deposit.",
      "Senior citizens can avail of a 0.50% extra SBI FD interest rates for an amount exceeding Rs.10,000.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate at source if Form 15G/15H is not submitted."
    ]
  },
  "hdfc": {
    fdFirstName: 'HDFC',
    key: 'hdfc',
    logo: `${IMAGE_BASE_URL}/hdfc.png`,
    fdCompoundingType: 'quarterly',
    minAmount: 5000,
    highestInterest: 7.90,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.0% - 7.90%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "5,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '18M',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '2Y 11M',
        generalInterest: 7.35,
        seniorInterest: 7.85,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '4Y 7M',
        generalInterest: 7.40,
        seniorInterest: 7.90,
        highlight: 'Max Return'
      },
      {
        id: 4,
        tenureYearFormat: '5Y 1D',
        generalInterest: 7.00,
        seniorInterest: 7.50,
        highlight: 'normal'
      }
    ],
    interestRates : [
      {tenure: "7 - 29 Days", generalInterest: 3.0, seniorInterest: 3.50, minTenure: '7D', maxTenure: '14D', highlight: 'normal' },
      {tenure: "30 - 45 Days", generalInterest: 3.50, seniorInterest: 4.0, minTenure: '30D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "61 - 89 Days", generalInterest: 4.50, seniorInterest: 5.0, minTenure: '61D', maxTenure: '89D', highlight: 'normal' },
      {tenure: "6 Months 1 Days - 9 Months", generalInterest: 5.75, seniorInterest: 6.25, minTenure: '6M 1D', maxTenure: '9M', highlight: 'normal' },
      {tenure: "1 Year - 15 Months", generalInterest: 6.60, seniorInterest: 7.10, minTenure: '1Y', maxTenure: '15M', highlight: 'normal' },
      {tenure: "15 Months - 18 Months", generalInterest: 7.10, seniorInterest: 7.60, minTenure: '15M', maxTenure: '18M', highlight: 'normal' },
      {tenure: "18 Months - 21 Months", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '18M', maxTenure: '21M', highlight: 'normal' },
      {tenure: "2 Years 1 Day - 2 Year 11 Months", generalInterest: 7.0, seniorInterest: 7.50, minTenure: '2Y 1D', maxTenure: '2Y 11M', highlight: 'normal' },
      {tenure: "2 Years 11 Months - 35 Months", generalInterest: 7.35, seniorInterest: 7.85, minTenure: '2Y 11M', maxTenure: '35M', highlight: 'normal' },
      {tenure: "3 Years 1 Day - 4 Years 7 Months", generalInterest: 7.0, seniorInterest: 7.50, minTenure: '3Y 1D', maxTenure: '4Y 7M', highlight: 'normal' },
      {tenure: "4 Year 7 Months - 55 Months", generalInterest: 7.40, seniorInterest: 7.90, minTenure: '4Y 7M', maxTenure: '55M', highlight: 'Max Return' },
      {tenure: "4 Year 7 Months 1 Day - 5 Years", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '4Y 7M 1D', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 Years 1 Day - 10 Years", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '5Y 1D', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "HDFC FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in HDFC is Rs 5000.",
      "Senior citizens can avail of a 0.50% extra HDFC FD interest rate.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate if Form 15G/15H is not submitted."
    ]
  },
  "icici": {
    fdFirstName: 'ICICI',
    key: 'icici',
    logo: `${IMAGE_BASE_URL}/icici.png`,
    fdCompoundingType: 'quarterly',
    minAmount: 10000,
    highestInterest: 7.85,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.0% - 7.75%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "10,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '1Y',
        generalInterest: 6.70,
        seniorInterest: 7.20,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '1Y 3M',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'Max Return'
      },
      {
        id: 3,
        tenureYearFormat: '1Y 6M',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'normal'
      },
      {
        id: 4,
        tenureYearFormat: '2Y 1D',
        generalInterest: 7.00,
        seniorInterest: 7.50,
        highlight: 'normal'
      }
    ],
    interestRates : [
      {tenure: "7 - 29 Days", generalInterest: 3.0, seniorInterest: 3.50, minTenure: '7D', maxTenure: '29D', highlight: 'normal' },
      {tenure: "30 - 45 Days", generalInterest: 3.50, seniorInterest: 4.0, minTenure: '30D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 60 Days", generalInterest: 4.25, seniorInterest: 4.75, minTenure: '46D', maxTenure: '60D', highlight: 'normal' },
      {tenure: "61 - 90 Days", generalInterest: 4.50, seniorInterest: 5.0, minTenure: '61D', maxTenure: '90M', highlight: 'normal' },
      {tenure: "91 - 184 Days", generalInterest: 4.75, seniorInterest: 5.25, minTenure: '91D', maxTenure: '184D', highlight: 'normal' },
      {tenure: "185 - 270 Days", generalInterest: 5.75, seniorInterest: 6.25, minTenure: '185D', maxTenure: '270D', highlight: 'normal' },
      {tenure: "271 Days - 1 Year", generalInterest: 6.00, seniorInterest: 6.50, minTenure: '271D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 Year - 15 Months", generalInterest: 6.70, seniorInterest: 7.20, minTenure: '1Y', maxTenure: '15M', highlight: 'normal' },
      {tenure: "15 Months - 18 Months", generalInterest: 7.25, seniorInterest: 7.85, minTenure: '15M', maxTenure: '18M', highlight: 'Max Return' },
      {tenure: "18 Months - 2 Years", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '18M', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "2 Years 1 Day - 5 Years", generalInterest: 7.0, seniorInterest: 7.50, minTenure: '2Y 1D', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 Years 1 Day - 10 Years", generalInterest: 6.90, seniorInterest: 7.40, minTenure: '5Y 1D', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "The ICICI Bank FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in ICICI Bank is Rs 10,000, with no limit on the maximum deposit.",
      "Senior citizens can avail of a 0.50% extra ICICI Bank FD interest rate.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate at source if Form 15G/15H is not submitted."
    ]
  },
  "axis": {
    fdFirstName: 'AXIS',
    key: 'axis',
    logo: `${IMAGE_BASE_URL}/axis.webp`,
    fdCompoundingType: 'quarterly',
    minAmount: 5000,
    highestInterest: 7.75,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.0% - 7.75%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "5,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '1Y',
        generalInterest: 6.70,
        seniorInterest: 7.20,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '1Y 3M',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'Max Return'
      },
      {
        id: 3,
        tenureYearFormat: '1Y 6M',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'normal'
      },
      {
        id: 4,
        tenureYearFormat: '2Y',
        generalInterest: 7.10,
        seniorInterest: 7.60,
        highlight: 'normal'
      }
    ],
    interestRates : [
      {tenure: "7 - 29 Days", generalInterest: 3.0, seniorInterest: 3.50, minTenure: '7D', maxTenure: '29D', highlight: 'normal' },
      {tenure: "30 - 45 Days", generalInterest: 3.50, seniorInterest: 4.0, minTenure: '30D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 60 Days", generalInterest: 4.25, seniorInterest: 4.75, minTenure: '46D', maxTenure: '60D', highlight: 'normal' },
      {tenure: "61 Days - 3 Months", generalInterest: 4.50, seniorInterest: 5.0, minTenure: '61D', maxTenure: '3M', highlight: 'normal' },
      {tenure: "3 Months - 6 Months", generalInterest: 4.75, seniorInterest: 5.25, minTenure: '3M', maxTenure: '6M', highlight: 'normal' },
      {tenure: "6 Months - 9 Months", generalInterest: 5.75, seniorInterest: 6.25, minTenure: '6M', maxTenure: '9M', highlight: 'normal' },
      {tenure: "9 Months - 1 Year", generalInterest: 6.00, seniorInterest: 6.50, minTenure: '9M', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 Year - 15 Months", generalInterest: 6.70, seniorInterest: 7.20, minTenure: '1Y', maxTenure: '15M', highlight: 'normal' },
      {tenure: "15 Months - 18 Months", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '15M', maxTenure: '18M', highlight: 'Max Return' },
      {tenure: "18 Months - 2 Years", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '18M', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "2 Years - 5 Years", generalInterest: 7.10, seniorInterest: 7.60, minTenure: '2Y', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 Years - 10 Years", generalInterest: 7.00, seniorInterest: 7.75, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "The Axis Bank FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in Axis Bank is Rs 5,000, with no limit on the maximum deposit.",
      "Senior citizens can avail of up to 0.75% extra Axis Bank FD interest rate.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate at source if Form 15G/15H is not submitted."
    ]
  },
  "kotak": {
    fdFirstName: 'KOTAK',
    key: 'kotak',
    logo: `${IMAGE_BASE_URL}/kotak.webp`,
    fdCompoundingType: 'quarterly',
    minAmount: 10000,
    highestInterest: 7.90,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "2.5% - 7.90%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "10,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '180D',
        generalInterest: 7.0,
        seniorInterest: 7.50,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '365D',
        generalInterest: 7.10,
        seniorInterest: 7.60,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '390D',
        generalInterest: 7.40,
        seniorInterest: 7.90,
        highlight: 'Max Return'
      },
      {
        id: 4,
        tenureYearFormat: '23M',
        generalInterest: 7.30,
        seniorInterest: 7.85,
        highlight: 'normal'
      }
    ],
    interestRates : [
      {tenure: "7 - 14 Days", generalInterest: 2.75, seniorInterest: 3.25, minTenure: '7D', maxTenure: '14D', highlight: 'normal' },
      {tenure: "15 - 30 Days", generalInterest: 3.00, seniorInterest: 3.50, minTenure: '15D', maxTenure: '30D', highlight: 'normal' },
      {tenure: "31 - 45 Days", generalInterest: 3.25, seniorInterest: 3.75, minTenure: '31D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 90 Days", generalInterest: 3.50, seniorInterest: 4.0, minTenure: '46D', maxTenure: '90D', highlight: 'normal' },
      {tenure: "91 - 120 Days", generalInterest: 4.0, seniorInterest: 4.50, minTenure: '91D', maxTenure: '120D', highlight: 'normal' },
      {tenure: "121 - 179 Days", generalInterest: 4.25, seniorInterest: 4.75, minTenure: '121D', maxTenure: '179D', highlight: 'normal' },
      {tenure: "180 Days", generalInterest: 7.0, seniorInterest: 7.50, minTenure: '180D', maxTenure: '180D', highlight: 'normal' },
      {tenure: "181 - 363 Days", generalInterest: 6.0, seniorInterest: 6.50, minTenure: '181D', maxTenure: '363D', highlight: 'normal' },
      {tenure: "364 Days", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '364D', maxTenure: '364D', highlight: 'normal' },
      {tenure: "365 - 389 Days", generalInterest: 7.10, seniorInterest: 7.60, minTenure: '365D', maxTenure: '389D', highlight: 'normal' },
      {tenure: "390 Days - 23 Months", generalInterest: 7.40, seniorInterest: 7.90, minTenure: '390D', maxTenure: '23M', highlight: 'Max Return' },
      {tenure: "23 Months - 2 Years", generalInterest: 7.30, seniorInterest: 7.85, minTenure: '23M', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "2 Years - 3 Years", generalInterest: 7.15, seniorInterest: 7.65, minTenure: '2Y', maxTenure: '3Y', highlight: 'normal' },
      {tenure: "3 Years - 5 Years", generalInterest: 7.0, seniorInterest: 7.60, minTenure: '3Y', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 Years - 10 Years", generalInterest: 6.20, seniorInterest: 6.70, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "The Kotak Bank FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in Kotak Bank is Rs 5,000, with no limit on the maximum deposit.",
      "Senior citizens can avail of a 0.50% extra Kotak Bank FD interest rate.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate at source if Form 15G/15H is not submitted."
    ]
  },
  "indusind": {
    fdFirstName: 'IndusInd',
    key: 'indusind',
    logo: `${IMAGE_BASE_URL}/indusind.webp`,
    fdCompoundingType: 'quarterly',
    minAmount: 10000,
    highestInterest: 8.25,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.5% - 8.49%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 61 Months" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "10,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '1Y',
        generalInterest: 7.75,
        seniorInterest: 8.25,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '1Y 5M',
        generalInterest: 7.99,
        seniorInterest: 8.49,
        highlight: 'Max Return'
      },
      {
        id: 3,
        tenureYearFormat: '1Y 6M',
        generalInterest: 7.75,
        seniorInterest: 8.25,
        highlight: 'normal'
      },
      {
        id: 4,
        tenureYearFormat: '61M',
        generalInterest: 7.0,
        seniorInterest: 7.50,
        highlight: 'normal'
      },
    ],
    interestRates : [
      {tenure: "7 - 14 Days", generalInterest: 3.50, seniorInterest: 4.00, minTenure: '7D', maxTenure: '14D', highlight: 'normal' },
      {tenure: "15 - 30 Days", generalInterest: 3.50, seniorInterest: 4.00, minTenure: '15D', maxTenure: '30D', highlight: 'normal' },
      {tenure: "31 - 45 Days", generalInterest: 3.75, seniorInterest: 4.25, minTenure: '31D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 120 Days", generalInterest: 4.75, seniorInterest: 5.25, minTenure: '46D', maxTenure: '120D', highlight: 'normal' },
      {tenure: "121 - 180 Days", generalInterest: 5.00, seniorInterest: 5.50, minTenure: '121D', maxTenure: '180D', highlight: 'normal' },
      {tenure: "181 - 210 Days", generalInterest: 5.85, seniorInterest: 6.35, minTenure: '181D', maxTenure: '210D', highlight: 'normal' },
      {tenure: "211 - 269 Days", generalInterest: 6.10, seniorInterest: 6.60, minTenure: '211D', maxTenure: '269D', highlight: 'normal' },
      {tenure: "270 - 354 Days", generalInterest: 6.35, seniorInterest: 6.85, minTenure: '270D', maxTenure: '354D', highlight: 'normal' },
      {tenure: "355 - 364 Days", generalInterest: 6.50, seniorInterest: 7.00, minTenure: '355D', maxTenure: '364D', highlight: 'normal' },
      {tenure: "1 Year - 2 Years", generalInterest: 7.75, seniorInterest: 8.25, minTenure: '1Y', maxTenure: '2Y', highlight: 'Max Return' },
      // {tenure: "1 Year 5 Months - 1 Year 6 Months", generalInterest: 7.99, seniorInterest: 8.49, minTenure: '1Y 5M', maxTenure: '1Y 6M', highlight: 'Max Return' },
      // {tenure: "1 Year 6 Months - 2 Years", generalInterest: 7.75, seniorInterest: 8.25, minTenure: '1Y 5M', maxTenure: '1Y 6M', highlight: 'normal' },
      {tenure: "2 Years - 61 Months", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '2Y', maxTenure: '61M', highlight: 'normal' },
      {tenure: "61 Months and Above", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '61M', maxTenure: '61M', highlight: 'normal' },
    ],
    features: [
      "Flexible tenures from 7 days to 61 months.",
      "Minimum deposit amount: Rs 10,000 with no upper limit.",
      "Senior citizens receive additional interest rate benefits.",
      "Option for auto-renewal of deposits.",
      "TDS deducted at the standard rate if applicable forms are not provided."
    ]
  },
  "bank_of_baroda": {
    fdFirstName: 'Bank of Baroda',
    key: 'bank_of_baroda',
    logo: `${IMAGE_BASE_URL}/baroda.png`,
    fdCompoundingType: 'quarterly',
    minAmount: 1000,
    highestInterest: 7.65,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "4.25% - 7.65%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "1,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '271D',
        generalInterest: 6.50,
        seniorInterest: 7.00,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '1Y',
        generalInterest: 7.00,
        seniorInterest: 7.50,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '2Y',
        generalInterest: 7.15,
        seniorInterest: 7.65,
        highlight: 'Max Return'
      },
      {
        id: 4,
        tenureYearFormat: '3Y',
        generalInterest: 6.80,
        seniorInterest: 7.40,
        highlight: 'normal'
      },
    ],
    interestRates : [
      {tenure: "7 - 14 Days", generalInterest: 4.25, seniorInterest: 4.75, minTenure: '7D', maxTenure: '14D', highlight: 'normal' },
      {tenure: "15 - 45 Days", generalInterest: 4.50, seniorInterest: 5.00, minTenure: '15D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 90 Days", generalInterest: 5.50, seniorInterest: 6.00, minTenure: '46D', maxTenure: '90D', highlight: 'normal' },
      {tenure: "91 - 180 Days", generalInterest: 5.60, seniorInterest: 6.10, minTenure: '91D', maxTenure: '180D', highlight: 'normal' },
      {tenure: "181 - 210 Days", generalInterest: 5.75, seniorInterest: 6.25, minTenure: '181D', maxTenure: '210D', highlight: 'normal' },
      {tenure: "211 - 270 Days", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '211D', maxTenure: '270D', highlight: 'normal' },
      {tenure: "271 Days - 1 Year", generalInterest: 6.50, seniorInterest: 7.00, minTenure: '271D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 - 2 Years", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '1Y', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "2 - 3 Years", generalInterest: 7.15, seniorInterest: 7.65, minTenure: '2Y', maxTenure: '3Y', highlight: 'Max Return' },
      {tenure: "3 - 5 Years", generalInterest: 6.80, seniorInterest: 7.40, minTenure: '3Y', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 - 10 Years", generalInterest: 6.50, seniorInterest: 7.50, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "Tenures ranging from 7 days to 10 years.",
      "Minimum deposit amount: Rs 1000 with no maximum limit.",
      "Senior citizens enjoy higher interest rates.",
      "Option for automatic renewal on maturity.",
      "Tax Deducted at Source (TDS) if applicable forms are not submitted."
    ]
  },
  "canara": {
    fdFirstName: 'Canara',
    key: 'canara',
    logo: `${IMAGE_BASE_URL}/CanaraBank.webp`,
    fdCompoundingType: 'quarterly',
    minAmount: 1000,
    highestInterest: 7.90,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "4.0% - 7.90%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "1,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '1Y',
        generalInterest: 6.85,
        seniorInterest: 7.35,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '444D',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '2Y',
        generalInterest: 7.30,
        seniorInterest: 7.80,
        highlight: 'normal'
      },
      {
        id: 4,
        tenureYearFormat: '3Y',
        generalInterest: 7.40,
        seniorInterest: 7.90,
        highlight: 'Max Return'
      },
    ],
    interestRates : [
      {tenure: "7 - 45 Days", generalInterest: 4.00, seniorInterest: 4.00, minTenure: '7D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 90 Days", generalInterest: 5.25, seniorInterest: 5.25, minTenure: '46D', maxTenure: '90D', highlight: 'normal' },
      {tenure: "91 - 179 Days", generalInterest: 5.50, seniorInterest: 5.50, minTenure: '91D', maxTenure: '179D', highlight: 'normal' },
      {tenure: "180 - 269 Days", generalInterest: 6.15, seniorInterest: 6.65, minTenure: '180D', maxTenure: '269D', highlight: 'normal' },
      {tenure: "270 - 1 Year", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '270D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 - 2 Years", generalInterest: 6.85, seniorInterest: 7.35, minTenure: '1Y', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "444 Days", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '444D', maxTenure: '444D', highlight: 'normal' },
      {tenure: "2 - 3 Years", generalInterest: 7.30, seniorInterest: 7.80, minTenure: '2Y', maxTenure: '3Y', highlight: 'normal' },
      {tenure: "3 - 5 Years", generalInterest: 7.40, seniorInterest: 7.90, minTenure: '3Y', maxTenure: '5Y', highlight: 'Max Return' },
      {tenure: "5 - 10 Years", generalInterest: 6.70, seniorInterest: 7.20, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "The Canara Bank FD tenures range from 7 days to 10 years.",
      "The minimum deposit amount needed to open an FD in Canara Bank is Rs 1000 and there is no limit on the maximum deposit.",
      "Senior citizens can avail of a higher interest rate on deposits.",
      "Auto-renewal facility is available on the deposit.",
      "TDS is deducted at the prevalent rate at source if Form 15G/15H is not submitted."
    ]
  },
  "pnb": {
    fdFirstName: 'PNB',
    key: 'pnb',
    logo: `${IMAGE_BASE_URL}/pnb.png`,
    fdCompoundingType: 'quarterly',
    minAmount: 1000,
    highestInterest: 7.75,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.5% - 7.75%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "1,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '300D',
        generalInterest: 7.05,
        seniorInterest: 7.55,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '400D',
        generalInterest: 7.25,
        seniorInterest: 7.75,
        highlight: 'Max Return'
      },
      {
        id: 3,
        tenureYearFormat: '2Y',
        generalInterest: 7.00,
        seniorInterest: 7.50,
        highlight: 'normal'
      },
      {
        id: 4,
        tenureYearFormat: '3Y',
        generalInterest: 6.50,
        seniorInterest: 7.0,
        highlight: 'normal'
      },
    ],
    interestRates : [
      {tenure: "7 - 45 Days", generalInterest: 3.50, seniorInterest: 4.00, minTenure: '7D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 90 Days", generalInterest: 4.50, seniorInterest: 5.00, minTenure: '46D', maxTenure: '90D', highlight: 'normal' },
      {tenure: "91 - 179 Days", generalInterest: 5.50, seniorInterest: 6.0, minTenure: '91D', maxTenure: '179D', highlight: 'normal' },
      {tenure: "180 - 270 Days", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '180D', maxTenure: '270D', highlight: 'normal' },
      {tenure: "271 - 299 Days", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '271D', maxTenure: '299D', highlight: 'normal' },
      {tenure: "300 Days", generalInterest: 7.05, seniorInterest: 7.55, minTenure: '300D', maxTenure: '300D', highlight: 'normal' },
      {tenure: "301 Days - 1 Year", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '301D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "1 Year - 399 Days", generalInterest: 6.50, seniorInterest: 7.0, minTenure: '1Y', maxTenure: '399D', highlight: 'normal' },
      {tenure: "400 Days", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '400D', maxTenure: '400D', highlight: 'Max Return' },
      {tenure: "401 Days - 2 Years", generalInterest: 6.80, seniorInterest: 7.30, minTenure: '401D', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "2 - 3 Years", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '2Y', maxTenure: '3Y', highlight: 'normal' },
      {tenure: "3 Years - 1203 Days", generalInterest: 6.50, seniorInterest: 7.00, minTenure: '3Y', maxTenure: '1203D', highlight: 'normal' },
    ],
    features: [
      "Flexible tenure options ranging from 7 days to 10 years.",
      "Minimum deposit of Rs 1,000 with no upper limit.",
      "Senior citizens receive additional interest benefits.",
      "Option for automatic renewal upon maturity.",
      "Tax Deducted at Source (TDS) if applicable forms are not provided."
    ]
  },
  "idbi": {
    fdFirstName: 'IDBI',
    key: 'idbi',
    logo: `${IMAGE_BASE_URL}/IDBI.jpg`,
    fdCompoundingType: 'quarterly',
    minAmount: 1000,
    highestInterest: 7.90,
    fdTopFeatures: [
      { "iconSrc": "/assets/percentage.png", "title": "Interest rates", "value": "3.0% - 7.90%" },
      { "iconSrc": "/assets/sand-clock.png", "title": "Time Period", "value": "7 days - 10 Years" },
      { "iconSrc": "/assets/rupee.png", "title": "Min. - Max amount", "value": "1,000 - No Limit" },
      { "iconSrc": "/assets/compounding-icon.png", "title": "Compounding", "value": "QUARTERLY" }
    ],
    fds: [
      {
        id: 1,
        tenureYearFormat: '375D',
        generalInterest: 7.25,
        seniorInterest: 7.25,
        highlight: 'normal'
      },
      {
        id: 2,
        tenureYearFormat: '444D',
        generalInterest: 7.35,
        seniorInterest: 7.85,
        highlight: 'normal'
      },
      {
        id: 3,
        tenureYearFormat: '555',
        generalInterest: 7.40,
        seniorInterest: 7.90,
        highlight: 'Max Return'
      },
      {
        id: 4,
        tenureYearFormat: '700',
        generalInterest: 7.20,
        seniorInterest: 7.70,
        highlight: 'normal'
      },
    ],
    interestRates : [
      {tenure: "7 - 30 Days", generalInterest: 3.0, seniorInterest: 3.50, minTenure: '7D', maxTenure: '30D', highlight: 'normal' },
      {tenure: "31 - 45 Days", generalInterest: 3.25, seniorInterest: 3.75, minTenure: '31D', maxTenure: '45D', highlight: 'normal' },
      {tenure: "46 - 60 Days", generalInterest: 4.50, seniorInterest: 5.0, minTenure: '46D', maxTenure: '60D', highlight: 'normal' },
      {tenure: "61 - 90 Days", generalInterest: 4.75, seniorInterest: 5.25, minTenure: '61D', maxTenure: '90D', highlight: 'normal' },
      {tenure: "91 Days - 6 Months", generalInterest: 5.50, seniorInterest: 6.0, minTenure: '91D', maxTenure: '6M', highlight: 'normal' },
      {tenure: "6 Months 1 Day - 270 Days", generalInterest: 6.00, seniorInterest: 6.50, minTenure: '6M 1D', maxTenure: '270D', highlight: 'normal' },
      {tenure: "271 Days - 1 Year", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '271D', maxTenure: '1Y', highlight: 'normal' },
      {tenure: "300 Days", generalInterest: 7.05, seniorInterest: 7.55, minTenure: '300D', maxTenure: '300D', highlight: 'normal' },
      {tenure: "1 Year - 2 Year", generalInterest: 6.80, seniorInterest: 7.30, minTenure: '1Y', maxTenure: '2Y', highlight: 'normal' },
      {tenure: "375 Days", generalInterest: 7.25, seniorInterest: 7.75, minTenure: '375D', maxTenure: '375D', highlight: 'normal' },
      {tenure: "444 Days", generalInterest: 7.35, seniorInterest: 7.85, minTenure: '444D', maxTenure: '444D', highlight: 'normal' },
      {tenure: "555 Days", generalInterest: 7.40, seniorInterest: 7.90, minTenure: '555D', maxTenure: '555D', highlight: 'Max Return' },
      {tenure: "700 Days", generalInterest: 7.20, seniorInterest: 7.70, minTenure: '700D', maxTenure: '700D', highlight: 'normal' },
      {tenure: "2 - 3 Years", generalInterest: 7.00, seniorInterest: 7.50, minTenure: '2Y', maxTenure: '3Y', highlight: 'normal' },
      {tenure: "3 - 5 Years", generalInterest: 6.50, seniorInterest: 7.00, minTenure: '3Y', maxTenure: '5Y', highlight: 'normal' },
      {tenure: "5 - 10 Years", generalInterest: 6.25, seniorInterest: 6.75, minTenure: '5Y', maxTenure: '10Y', highlight: 'normal' },
    ],
    features: [
      "Flexible tenure options ranging from 7 days to 10 years.",
      "Minimum deposit of Rs 1,000 with no upper limit.",
      "Senior citizens receive additional interest benefits.",
      "Option for automatic renewal upon maturity.",
      "Tax Deducted at Source (TDS) if applicable forms are not provided."
    ]
  },
  
}